/**
 * StableDataFetchBar – shown when data source is Stable (CBOE).
 * Single typeahead: type ticker → suggestions → select with Enter or click. Then expiry/strikes and Fetch.
 */

import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from "react";
import { useDataSource } from "../contexts/DataSourceContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { dataService } from "../services/dataService";
import {
    isFuturesModeEnabled,
    getSelectedFutures,
    getOptionsUnderlying,
} from "../utils/unifiedSymbolMapping";
import {
    readStableUnderlyingsSessionFirst,
    setStableTabSelection,
} from "../utils/stableTabStorage";
import { persistStrikePrefsForSymbol } from "../utils/stableStrikeParams";
import { getApiUrl } from "../config/api";
import type { ColorScheme } from "../utils/colorScheme";
import {
    describeStableProgress,
    type StableFetchProgress,
} from "../services/stableFetchProgress";

const EXPIRY_OPTIONS = [
    { value: "weekly", labelEn: "Weekly only", labelAr: "أسبوعي فقط" },
    { value: "this-week", labelEn: "This week", labelAr: "هذا الأسبوع" },
    { value: "monthly", labelEn: "Monthly", labelAr: "شهري" },
    { value: "this-month", labelEn: "This month", labelAr: "هذا الشهر" },
    { value: "3-months", labelEn: "3 months", labelAr: "3 أشهر" },
    { value: "quarter", labelEn: "Quarter", labelAr: "ربع سنة" },
    { value: "6-months", labelEn: "6 months", labelAr: "نصف سنة" },
    { value: "annual", labelEn: "1 year", labelAr: "سنة واحدة" },
    { value: "specific-date", labelEn: "Specific date", labelAr: "تاريخ محدد" },
    {
        value: "custom",
        labelEn: "Custom (pick dates)",
        labelAr: "مخصص (اختر التواريخ)",
    },
];

const STRIKE_RANGE_OPTIONS = [
    10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 100, 150, 200, 250, 300, 400,
    500, 600, 800, 1000,
] as const;

export const STABLE_FETCH_EVENT = "stableDataFetchRequested";
export const STABLE_FETCH_CANCEL_EVENT = "stableDataFetchCancelRequested";
const STABLE_FETCH_STATUS_EVENT = "stableDataFetchStatus";
const MAX_LOG_LINES = 12;

interface StableDataFetchBarProps {
    /** MMEC simple shell: compact strip + expand for full controls */
    compactLayout?: boolean;
}

/** Format an ISO date string (YYYY-MM-DD) as "Fri Jul 31, 2026". */
const formatExpiryLabel = (iso: string): string => {
    const d = new Date(`${iso}T00:00:00`);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

interface CustomExpiryPanelProps {
    available: string[];
    selected: string[];
    onChange: (next: string[]) => void;
    loading: boolean;
    error: string;
    language: string;
    colors: ColorScheme;
    styles: {
        label: React.CSSProperties;
        input: React.CSSProperties;
        btnGhost: React.CSSProperties;
    };
}

/**
 * Multi-select panel for the "custom" expiry option (Feature 1).
 * Shows available future expirations as a scrollable checkbox list.
 */
const CustomExpiryPanel: React.FC<CustomExpiryPanelProps> = ({
    available,
    selected,
    onChange,
    loading,
    error,
    language,
    colors,
    styles,
}) => {
    const ar = language === "ar";
    const toggle = (date: string) => {
        if (selected.includes(date)) {
            onChange(selected.filter((d) => d !== date));
        } else {
            onChange([...selected, date].sort());
        }
    };
    const selectAll = () => onChange([...available].sort());
    const clearAll = () => onChange([]);

    const countLabel = ar
        ? `${selected.length} / ${available.length} محدد`
        : `${selected.length} / ${available.length} selected`;

    return (
        <div className="flex flex-col gap-1 basis-full min-w-[220px]">
            <div className="flex items-center justify-between gap-2">
                <span style={styles.label}>
                    {ar ? "تواريخ الانتهاء" : "Expiry dates"}
                </span>
                <span
                    style={{
                        fontSize: 11,
                        color: colors.textMuted || colors.textSecondary,
                    }}
                >
                    {countLabel}
                </span>
            </div>
            <div
                className="flex items-center gap-2"
                style={{ marginBottom: 4 }}
            >
                <button
                    type="button"
                    onClick={selectAll}
                    disabled={loading || available.length === 0}
                    style={{
                        ...styles.btnGhost,
                        minHeight: 32,
                        padding: "4px 10px",
                        fontSize: 12,
                        opacity:
                            loading || available.length === 0 ? 0.5 : 1,
                    }}
                >
                    {ar ? "تحديد الكل" : "Select All"}
                </button>
                <button
                    type="button"
                    onClick={clearAll}
                    disabled={loading || selected.length === 0}
                    style={{
                        ...styles.btnGhost,
                        minHeight: 32,
                        padding: "4px 10px",
                        fontSize: 12,
                        opacity:
                            loading || selected.length === 0 ? 0.5 : 1,
                    }}
                >
                    {ar ? "مسح" : "Clear"}
                </button>
            </div>
            <div
                style={{
                    borderRadius: 8,
                    border: `1px solid ${colors.surfaceBorder}`,
                    backgroundColor: colors.background,
                    maxHeight: 200,
                    overflowY: "auto",
                }}
            >
                {loading ? (
                    <div
                        style={{
                            padding: "12px 14px",
                            fontSize: 13,
                            color: colors.textSecondary,
                        }}
                    >
                        {ar ? "جارٍ التحميل…" : "Loading…"}
                    </div>
                ) : error ? (
                    <div
                        style={{
                            padding: "12px 14px",
                            fontSize: 13,
                            color: colors.warning || "#dc2626",
                        }}
                    >
                        {ar ? "خطأ في التحميل" : "Load error"}: {error}
                    </div>
                ) : available.length === 0 ? (
                    <div
                        style={{
                            padding: "12px 14px",
                            fontSize: 13,
                            color: colors.textMuted || colors.textSecondary,
                        }}
                    >
                        {ar
                            ? "لا تواريخ متاحة"
                            : "No future expirations available"}
                    </div>
                ) : (
                    available.map((date) => {
                        const checked = selected.includes(date);
                        return (
                            <label
                                key={date}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    borderBottom: `1px solid ${colors.surfaceBorder}`,
                                    backgroundColor: checked
                                        ? colors.background
                                        : "transparent",
                                    WebkitTapHighlightColor: "transparent",
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggle(date)}
                                    style={{ width: 16, height: 16 }}
                                />
                                <span
                                    style={{
                                        fontSize: 13,
                                        color: colors.text,
                                    }}
                                >
                                    {formatExpiryLabel(date)}
                                </span>
                            </label>
                        );
                    })
                )}
            </div>
        </div>
    );
};

const StableDataFetchBar: React.FC<StableDataFetchBarProps> = ({
    compactLayout = false,
}) => {
    const { dataSource } = useDataSource();
    const { isDarkMode, colors } = useTheme();
    const { language } = useLanguage();
    const [availableUnderlyings, setAvailableUnderlyings] = useState<string[]>(
        [],
    );
    const [selectedUnderlying, setSelectedUnderlying] = useState<string>(() => {
        const list = readStableUnderlyingsSessionFirst();
        return list[0] || "";
    });
    const [expiryRange, setExpiryRange] = useState<string>(() => {
        return localStorage.getItem("mmec_expiry_range") || "weekly";
    });
    const [expiryDate, setExpiryDate] = useState<string>(
        () => localStorage.getItem("mmec_expiry_date") || "",
    );

    /**
     * Guarantee a date whenever Specific date is the active mode.
     *
     * The request only carries `expiry` when one is stored, so selecting the mode without touching
     * the picker sent `expiry_range=specific-date` with no date at all — and the server answers
     * that by returning every expiry it has. Asking for one day and receiving twenty-seven looks
     * like the filter is broken. Defaulting to today makes the request say what the mode claims.
     */
    const ensureSpecificDate = useCallback(() => {
        const stored = (localStorage.getItem("mmec_expiry_date") || "").trim();
        if (stored) return stored;
        const today = new Date().toISOString().slice(0, 10);
        localStorage.setItem("mmec_expiry_date", today);
        setExpiryDate(today);
        return today;
    }, []);
    const [strikeRange, setStrikeRange] = useState<string>(() => {
        const saved = localStorage.getItem("mmec_strike_range") || "150";
        return saved;
    });
    const [strikeMode, setStrikeMode] = useState<"count" | "all" | "custom">(
        () => {
            const m = localStorage.getItem("mmec_strike_mode") || "count";
            return (m === "all" || m === "custom" ? m : "count") as
                | "count"
                | "all"
                | "custom";
        },
    );
    const [strikeMin, setStrikeMin] = useState<string>(
        () => localStorage.getItem("mmec_strike_min") || "400",
    );
    const [strikeMax, setStrikeMax] = useState<string>(
        () => localStorage.getItem("mmec_strike_max") || "550",
    );
    // Custom multi-expiry state (Feature 1)
    const [customExpiries, setCustomExpiries] = useState<string[]>(() => {
        const range = localStorage.getItem("mmec_expiry_range");
        if (range !== "custom") return [];
        const saved = localStorage.getItem("mmec_expiry_date") || "";
        return saved
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .sort();
    });
    const [availableExpiries, setAvailableExpiries] = useState<string[]>([]);
    const [customExpiriesLoading, setCustomExpiriesLoading] = useState(false);
    const [customExpiriesError, setCustomExpiriesError] = useState<string>("");
    // Snapshot date / time — hidden behind a checkbox, unchecked by default (live mode).
    const [snapshotEnabled, setSnapshotEnabled] = useState<boolean>(false);
    const [snapshotDate, setSnapshotDate] = useState<string>(
        () => localStorage.getItem("mmec_snapshot_date") || "",
    );
    const [loading, setLoading] = useState(false);
    const [loadingList, setLoadingList] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [fetchStatus, setFetchStatus] = useState<
        "idle" | "loading" | "success" | "error" | "cancelled"
    >("idle");
    // Timestamp of the last successful fetch — shown to the user for transparency.
    const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
    /** Expiry progress of the last ranged fetch ("12 of 38 expiries loaded"). */
    const [expiryProgress, setExpiryProgress] =
        useState<StableFetchProgress | null>(null);
    const [logLines, setLogLines] = useState<string[]>([]);
    /** Floating fetch log panel (fixed; does not consume bar layout). */
    const [fetchLogOpen, setFetchLogOpen] = useState(false);
    const [compactExpanded, setCompactExpanded] = useState(false);
    const typeaheadRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Narrow underlyings by typed letters (case-insensitive); keep current selection in list if it exists; cap for dropdown
    const filteredUnderlyings = React.useMemo(() => {
        const q = (filterText || "").trim().toUpperCase();
        let list = !q
            ? availableUnderlyings
            : availableUnderlyings.filter((u) => u.toUpperCase().includes(q));
        if (selectedUnderlying && !list.includes(selectedUnderlying)) {
            list = [selectedUnderlying, ...list];
        }
        return list.slice(0, 80);
    }, [availableUnderlyings, filterText, selectedUnderlying]);

    /**
     * Load available option expirations for a symbol, filtered to today onward.
     * Used by the "custom" expiry multi-select (Feature 1).
     */
    const loadAvailableExpiries = useCallback(async (symbol: string) => {
        const sym = symbol.trim().toUpperCase();
        if (!sym) {
            setAvailableExpiries([]);
            return;
        }
        setCustomExpiriesLoading(true);
        setCustomExpiriesError("");
        try {
            const url = getApiUrl(
                `/api/theta/proxy?path=option/list/expirations&symbol=${encodeURIComponent(sym)}&format=json`,
            );
            const res = await fetch(url);
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const payload = await res.json();
            const list: Array<{ expiration?: string } | string> =
                payload?.response || payload || [];
            const todayStr = new Date().toISOString().slice(0, 10);
            const dates = list
                .map((entry) =>
                    typeof entry === "string" ? entry : entry?.expiration || "",
                )
                .filter((d) => typeof d === "string" && d.length >= 10)
                .map((d) => d.slice(0, 10))
                .filter((d) => d >= todayStr)
                .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
            // de-dup
            const uniq = Array.from(new Set(dates));
            setAvailableExpiries(uniq);
            // prune any previously-selected dates that are no longer offered
            setCustomExpiries((prev) =>
                prev.filter((d) => uniq.includes(d)),
            );
        } catch (err) {
            setAvailableExpiries([]);
            setCustomExpiriesError(
                err instanceof Error ? err.message : String(err),
            );
        } finally {
            setCustomExpiriesLoading(false);
        }
    }, []);

    const selectUnderlying = useCallback((symbol: string) => {
        setSelectedUnderlying(symbol);
        setFilterText("");
        setDropdownOpen(false);
        // If user is in custom expiry mode, reload expirations for the new
        // symbol and clear stale selections.
        setExpiryRange((currentRange) => {
            if (currentRange === "custom") {
                setCustomExpiries([]);
                localStorage.removeItem("mmec_expiry_date");
                loadAvailableExpiries(symbol);
            }
            return currentRange;
        });
    }, [loadAvailableExpiries]);

    const handleTypeaheadKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
                e.preventDefault();
                const typed = (filterText || "").trim().toUpperCase();
                if (typed) {
                    if (
                        filteredUnderlyings.length > 0 &&
                        filteredUnderlyings.includes(typed)
                    ) {
                        selectUnderlying(typed);
                    } else {
                        setSelectedUnderlying(typed);
                        setFilterText("");
                        setDropdownOpen(false);
                    }
                    setDropdownOpen(false);
                    // Trigger fetch with the symbol (typed or first suggestion)
                    const symbolToFetch =
                        typed ||
                        (filteredUnderlyings.length > 0
                            ? filteredUnderlyings[0]
                            : "");
                    if (symbolToFetch && !loading) {
                        setTimeout(() => {
                            setStableTabSelection([symbolToFetch]);
                            localStorage.setItem(
                                "mmec_expiry_range",
                                expiryRange,
                            );
                            if (expiryRange === "specific-date" && expiryDate) {
                                localStorage.setItem(
                                    "mmec_expiry_date",
                                    expiryDate,
                                );
                            } else if (expiryRange === "custom") {
                                if (customExpiries.length > 0) {
                                    localStorage.setItem(
                                        "mmec_expiry_date",
                                        customExpiries.join(","),
                                    );
                                } else {
                                    localStorage.removeItem("mmec_expiry_date");
                                }
                            } else {
                                localStorage.removeItem("mmec_expiry_date");
                            }
                            localStorage.setItem(
                                "mmec_strike_mode",
                                strikeMode,
                            );
                            if (strikeMode === "custom") {
                                localStorage.setItem(
                                    "mmec_strike_min",
                                    strikeMin,
                                );
                                localStorage.setItem(
                                    "mmec_strike_max",
                                    strikeMax,
                                );
                            } else if (strikeMode === "count") {
                                localStorage.setItem(
                                    "mmec_strike_range",
                                    strikeRange,
                                );
                            }
                            // Coerce after writes: index + custom → count
                            if (persistStrikePrefsForSymbol(symbolToFetch)) {
                                setStrikeMode("count");
                            }
                            if (snapshotDate)
                                localStorage.setItem(
                                    "mmec_snapshot_date",
                                    snapshotDate,
                                );
                            window.dispatchEvent(
                                new CustomEvent(STABLE_FETCH_EVENT, {
                                    detail: {
                                        underlying: symbolToFetch,
                                        expiryRange,
                                        expiryDate,
                                        customExpiries,
                                        snapshotDate,
                                    },
                                }),
                            );
                        }, 0);
                    }
                } else if (filteredUnderlyings.length > 0) {
                    const first = filteredUnderlyings[0];
                    selectUnderlying(first);
                    if (first && !loading) {
                        setTimeout(() => {
                            setStableTabSelection([first]);
                            localStorage.setItem(
                                "mmec_expiry_range",
                                expiryRange,
                            );
                            if (expiryRange === "specific-date" && expiryDate) {
                                localStorage.setItem(
                                    "mmec_expiry_date",
                                    expiryDate,
                                );
                            } else if (expiryRange === "custom") {
                                if (customExpiries.length > 0) {
                                    localStorage.setItem(
                                        "mmec_expiry_date",
                                        customExpiries.join(","),
                                    );
                                } else {
                                    localStorage.removeItem("mmec_expiry_date");
                                }
                            } else {
                                localStorage.removeItem("mmec_expiry_date");
                            }
                            localStorage.setItem(
                                "mmec_strike_mode",
                                strikeMode,
                            );
                            if (strikeMode === "custom") {
                                localStorage.setItem(
                                    "mmec_strike_min",
                                    strikeMin,
                                );
                                localStorage.setItem(
                                    "mmec_strike_max",
                                    strikeMax,
                                );
                            } else if (strikeMode === "count") {
                                localStorage.setItem(
                                    "mmec_strike_range",
                                    strikeRange,
                                );
                            }
                            // Coerce after writes: index + custom → count
                            if (persistStrikePrefsForSymbol(first)) {
                                setStrikeMode("count");
                            }
                            if (snapshotDate)
                                localStorage.setItem(
                                    "mmec_snapshot_date",
                                    snapshotDate,
                                );
                            window.dispatchEvent(
                                new CustomEvent(STABLE_FETCH_EVENT, {
                                    detail: {
                                        underlying: first,
                                        expiryRange,
                                        expiryDate,
                                        customExpiries,
                                        snapshotDate,
                                    },
                                }),
                            );
                        }, 0);
                    }
                }
            } else if (e.key === "Escape") {
                setDropdownOpen(false);
                setFilterText(selectedUnderlying || "");
            }
        },
        [
            filteredUnderlyings,
            filterText,
            selectedUnderlying,
            loading,
            expiryRange,
            expiryDate,
            strikeMode,
            strikeMin,
            strikeMax,
            strikeRange,
            customExpiries,
            snapshotDate,
        ],
    );

    const loadUnderlyings = useCallback(async () => {
        setLoadingList(true);
        const applyList = (list: string[]) => {
            setAvailableUnderlyings(list);
            setSelectedUnderlying((prev) => {
                if (!list?.length) return prev || "";
                if (prev && list.includes(prev)) return prev;
                if (prev && !list.includes(prev)) return list[0];
                return prev || "";
            });
        };
        try {
            // Load from pre-built static file first (instant, no API roundtrip)
            const staticRes = await fetch("/stable_underlyings.json", {
                cache: "force-cache",
            });
            if (staticRes.ok) {
                const staticList: string[] = await staticRes.json();
                if (Array.isArray(staticList) && staticList.length > 0) {
                    applyList(staticList);
                    setLoadingList(false);
                    // Background refresh from backend (non-blocking) to pick up any new symbols
                    dataService
                        .fetchAvailableUnderlyings("stable")
                        .then((fresh) => {
                            if (fresh && fresh.length > 0) applyList(fresh);
                        })
                        .catch(() => {});
                    return;
                }
            }
        } catch {
            // Static file not available, fall through to API
        }
        try {
            const list = await dataService.fetchAvailableUnderlyings("stable");
            applyList(list || []);
        } catch (e) {
            console.error("[StableDataFetchBar] Failed to load underlyings", e);
            setAvailableUnderlyings([]);
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => {
        if (dataSource === "stable") {
            loadUnderlyings();
        }
    }, [dataSource, loadUnderlyings]);

    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (dataSource !== "stable" || !fetchLogOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setFetchLogOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [dataSource, fetchLogOpen]);

    // When switching to Stable, clear any stale underlying so we don't show RTL symbol; sync with CBOE list when it loads
    useEffect(() => {
        if (dataSource !== "stable") return;
        if (readStableUnderlyingsSessionFirst().length === 0) {
            setSelectedUnderlying("");
        }
    }, [dataSource]);

    // Only fix invalid symbol (not in list); allow empty so user can clear the bar
    useEffect(() => {
        if (dataSource !== "stable" || !availableUnderlyings.length) return;
        if (selectedUnderlying === "") return;
        if (!availableUnderlyings.includes(selectedUnderlying)) {
            setSelectedUnderlying(availableUnderlyings[0]);
        }
    }, [dataSource, availableUnderlyings, selectedUnderlying]);

    // Status log from App (loadData) so user sees what's going on
    useEffect(() => {
        const handler = (e: Event) => {
            const ev = e as CustomEvent<{
                message: string;
                phase: string;
                rows?: number;
                progress?: StableFetchProgress | null;
            }>;
            const { message, phase, progress } = ev?.detail || {};
            if (!message) return;
            if (phase === "start" && !progress) {
                // A new fetch starts with no progress yet; drop the previous fetch's count.
                if (/^Fetching/.test(message)) setExpiryProgress(null);
            } else if (progress !== undefined) {
                setExpiryProgress(progress ?? null);
            }
            setLogLines((prev) => [
                ...prev.slice(-(MAX_LOG_LINES - 1)),
                `[${new Date().toLocaleTimeString()}] ${message}`,
            ]);
            if (phase === "start") {
                setLoading(true);
                setFetchStatus("loading");
            } else if (phase === "success") {
                setLoading(false);
                setFetchStatus("success");
                setFetchedAt(new Date());
            } else if (phase === "error" || phase === "cancel") {
                setLoading(false);
                setFetchStatus(phase === "cancel" ? "cancelled" : "error");
            }
        };
        window.addEventListener(STABLE_FETCH_STATUS_EVENT, handler);
        return () =>
            window.removeEventListener(STABLE_FETCH_STATUS_EVENT, handler);
    }, []);

    const handleFetch = () => {
        const symbol = (selectedUnderlying || filterText || "")
            .trim()
            .toUpperCase();
        if (!symbol) return;
        localStorage.setItem("mmec_strike_mode", strikeMode);
        if (strikeMode === "custom") {
            localStorage.setItem("mmec_strike_min", strikeMin);
            localStorage.setItem("mmec_strike_max", strikeMax);
        } else if (strikeMode === "count") {
            localStorage.setItem("mmec_strike_range", strikeRange);
        }
        // Auto-coerce after writing state-derived prefs: if the symbol is a
        // high-strike index with SPY-scale custom band, overwrite to count.
        if (persistStrikePrefsForSymbol(symbol)) {
            setStrikeMode("count");
        }
        setLoading(true);
        setFetchStatus("loading");
        setLogLines((prev) => [
            ...prev.slice(-(MAX_LOG_LINES - 1)),
            `[${new Date().toLocaleTimeString()}] Requesting ${symbol}…`,
        ]);
        setStableTabSelection([symbol]);
        localStorage.setItem("mmec_expiry_range", expiryRange);
        if (expiryRange === "specific-date" && expiryDate) {
            localStorage.setItem("mmec_expiry_date", expiryDate);
        } else if (expiryRange === "custom") {
            // Store selected custom expiries as a comma-separated string.
            if (customExpiries.length > 0) {
                localStorage.setItem(
                    "mmec_expiry_date",
                    customExpiries.join(","),
                );
            } else {
                localStorage.removeItem("mmec_expiry_date");
            }
        } else {
            localStorage.removeItem("mmec_expiry_date");
        }
        // Persist snapshot date (Feature 2)
        if (snapshotDate) {
            localStorage.setItem("mmec_snapshot_date", snapshotDate);
        } else {
            localStorage.removeItem("mmec_snapshot_date");
        }
        window.dispatchEvent(
            new CustomEvent(STABLE_FETCH_EVENT, {
                detail: {
                    underlying: symbol,
                    expiryRange,
                    expiryDate,
                    customExpiries,
                    snapshotDate,
                },
            }),
        );
    };

    const handleCancel = () => {
        window.dispatchEvent(new CustomEvent(STABLE_FETCH_CANCEL_EVENT));
    };

    const handleTryAgain = () => {
        handleFetch();
    };

    const stableStyles = useMemo(() => {
        // Simple mode keeps ~40px touch targets; the Pro toolbar sits at 32px so
        // it reads as chrome rather than a form the eye has to pass through.
        const h = compactLayout ? 40 : 32;
        const fs = compactLayout ? 13 : 12.5;
        const control: React.CSSProperties = {
            borderRadius: 8,
            border: "1px solid var(--hair)",
            backgroundColor: "var(--base)",
            color: "var(--txt)",
            fontSize: fs,
            fontFamily: "var(--font-ui)",
            padding: compactLayout ? "0 12px" : "0 10px",
            height: h,
            minHeight: h,
            lineHeight: 1.2,
            WebkitTapHighlightColor: "transparent",
            touchAction: "manipulation" as const,
        };
        return {
            shell: {
                borderBottom: "1px solid var(--hair)",
                backgroundColor: "var(--elev)",
                color: "var(--txt)",
                paddingLeft: `max(12px, env(safe-area-inset-left, 0px))`,
                paddingRight: `max(12px, env(safe-area-inset-right, 0px))`,
                paddingTop: 8,
                paddingBottom: 8,
            } as React.CSSProperties,
            label: {
                fontFamily: "var(--font-ui)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.09em",
                textTransform: "uppercase" as const,
                color: "var(--muted-2)",
                whiteSpace: "nowrap" as const,
                lineHeight: 1,
            } as React.CSSProperties,
            input: control,
            select: { ...control, paddingRight: 26 },
            btnPrimary: {
                borderRadius: 8,
                border: "1px solid transparent",
                backgroundColor: "var(--acc)",
                color: "var(--void)",
                fontFamily: "var(--font-ui)",
                fontWeight: 600,
                fontSize: fs,
                padding: compactLayout ? "0 16px" : "0 14px",
                height: h,
                minHeight: h,
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                touchAction: "manipulation" as const,
            } as React.CSSProperties,
            btnGhost: {
                borderRadius: 8,
                border: "1px solid var(--hair)",
                backgroundColor: "transparent",
                color: "var(--txt-2)",
                fontFamily: "var(--font-ui)",
                fontSize: fs,
                padding: compactLayout ? "0 12px" : "0 10px",
                height: h,
                minHeight: h,
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                touchAction: "manipulation" as const,
            } as React.CSSProperties,
            logBox: {
                borderRadius: 8,
                border: "1px solid var(--hair)",
                backgroundColor: "var(--base)",
                color: "var(--txt-2)",
                fontSize: 12,
                fontFamily: "var(--font-mono)",
                maxHeight: 96,
                overflowY: "auto" as const,
                padding: 8,
            } as React.CSSProperties,
            dropdown: {
                position: "absolute" as const,
                left: 0,
                zIndex: 50,
                marginTop: 4,
                maxHeight: 240,
                overflowY: "auto" as const,
                borderRadius: 10,
                border: "1px solid var(--border)",
                backgroundColor: "var(--elev)",
                boxShadow: "var(--lift-3)",
                width: "min(100%, 240px)",
                padding: 4,
            } as React.CSSProperties,
            dropdownItem: (active: boolean) =>
                ({
                    padding: compactLayout ? "10px 12px" : "7px 10px",
                    fontSize: fs,
                    fontFamily: "var(--font-mono)",
                    borderRadius: 6,
                    cursor: "pointer",
                    color: active ? "var(--txt)" : "var(--txt-2)",
                    backgroundColor: active ? "var(--acc-dim)" : "transparent",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                }) as React.CSSProperties,
        };
    }, [compactLayout]);

    // "x of N expiries loaded" while a ranged fetch fills in; the final count afterwards,
    // with the per-symbol ceiling note when it applied. Hidden for single-expiry fetches.
    const expiryProgressBadge =
        expiryProgress &&
        expiryProgress.total > 1 &&
        (fetchStatus === "loading" || fetchStatus === "success") ? (
            <span
                title={
                    expiryProgress.pending.length
                        ? (language === "ar" ? "قيد التحميل: " : "Still loading: ") +
                          expiryProgress.pending.join(", ")
                        : undefined
                }
                style={{
                    fontSize: 11,
                    color:
                        expiryProgress.partial || expiryProgress.beyondCeiling > 0
                            ? colors.warning || "#b45309"
                            : colors.textMuted,
                    whiteSpace: "nowrap",
                    alignSelf: "center",
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {describeStableProgress(expiryProgress, language)}
            </span>
        ) : null;

    const fetchLogFloat =
        dataSource === "stable" ? (
            <>
                <button
                    type="button"
                    aria-expanded={fetchLogOpen}
                    aria-label={language === "ar" ? "سجل الجلب" : "Fetch log"}
                    title={
                        language === "ar"
                            ? "فتح/إغلاق سجل الجلب"
                            : "Toggle fetch log"
                    }
                    onClick={() => setFetchLogOpen((o) => !o)}
                    style={{
                        position: "fixed",
                        // Sit just past the Pro nav rail (the shell publishes its width)
                        // so the rail's footer stays clickable; 0 in Simple mode.
                        left: "calc(var(--mmec-rail-offset, 0px) + max(12px, env(safe-area-inset-left, 0px)))",
                        bottom: "calc(var(--mmec-bottom-offset, 0px) + max(12px, env(safe-area-inset-bottom, 0px)))",
                        zIndex: 100,
                        width: 44,
                        height: 44,
                        borderRadius: 999,
                        border: `1px solid ${colors.surfaceBorder}`,
                        backgroundColor: isDarkMode
                            ? "rgba(30,41,59,0.92)"
                            : "rgba(255,255,255,0.94)",
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        boxShadow: isDarkMode
                            ? "0 4px 20px rgba(0,0,0,0.45)"
                            : "0 4px 18px rgba(15,23,42,0.12)",
                        color: colors.text,
                        fontSize: 18,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1,
                    }}
                >
                    {fetchLogOpen ? "✕" : "📋"}
                </button>
                {fetchLogOpen && (
                    <div
                        role="dialog"
                        aria-label={
                            language === "ar" ? "سجل الجلب" : "Fetch log"
                        }
                        style={{
                            position: "fixed",
                            left: "max(12px, env(safe-area-inset-left, 0px))",
                            bottom: "max(64px, calc(12px + env(safe-area-inset-bottom, 0px) + 44px))",
                            zIndex: 101,
                            width: "min(360px, calc(100vw - 24px))",
                            maxHeight: "min(48vh, 280px)",
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: 12,
                            border: `1px solid ${colors.surfaceBorder}`,
                            backgroundColor: isDarkMode
                                ? "rgba(30,41,59,0.94)"
                                : "rgba(255,255,255,0.96)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            boxShadow: isDarkMode
                                ? "0 12px 40px rgba(0,0,0,0.5)"
                                : "0 12px 36px rgba(15,23,42,0.14)",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                                padding: "8px 10px",
                                borderBottom: `1px solid ${colors.surfaceBorder}`,
                                flexShrink: 0,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                    color: colors.textMuted,
                                }}
                            >
                                {language === "ar" ? "سجل الجلب" : "Fetch log"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setFetchLogOpen(false)}
                                style={{
                                    ...stableStyles.btnGhost,
                                    minHeight: 32,
                                    padding: "4px 10px",
                                    fontSize: 12,
                                }}
                            >
                                {language === "ar" ? "إغلاق" : "Close"}
                            </button>
                        </div>
                        <div
                            style={{
                                ...stableStyles.logBox,
                                border: "none",
                                borderRadius: 0,
                                maxHeight: "none",
                                flex: 1,
                                minHeight: 0,
                                overflowY: "auto",
                            }}
                        >
                            {logLines.length === 0 && !loading ? (
                                <p
                                    style={{
                                        margin: 0,
                                        fontSize: 12,
                                        color: colors.textMuted,
                                    }}
                                >
                                    {language === "ar"
                                        ? "لا نشاط بعد. اضغط جلب لرؤية السجل هنا."
                                        : "No activity yet. Fetch to see steps here."}
                                </p>
                            ) : (
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {logLines
                                        .slice(-MAX_LOG_LINES)
                                        .map((line, i) => (
                                            <li key={i}>{line}</li>
                                        ))}
                                    {loading &&
                                        logLines[logLines.length - 1]?.includes(
                                            "Fetching",
                                        ) && (
                                            <li
                                                style={{
                                                    color: colors.warning,
                                                }}
                                            >
                                                …
                                            </li>
                                        )}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </>
        ) : null;

    if (dataSource !== "stable") return null;

    if (isFuturesModeEnabled()) {
        const selected = getSelectedFutures();
        const mappings = selected.map((f) => ({
            future: f,
            underlying: getOptionsUnderlying(f),
        }));
        const handleFuturesFetch = () => {
            const underlyings = Array.from(
                new Set(mappings.map((m) => m.underlying)),
            );
            if (!underlyings.length) return;
            setStableTabSelection(underlyings);
            localStorage.setItem("mmec_expiry_range", expiryRange);
            if (expiryRange === "specific-date" && expiryDate) {
                localStorage.setItem("mmec_expiry_date", expiryDate);
            } else if (expiryRange === "custom") {
                if (customExpiries.length > 0) {
                    localStorage.setItem(
                        "mmec_expiry_date",
                        customExpiries.join(","),
                    );
                } else {
                    localStorage.removeItem("mmec_expiry_date");
                }
            } else {
                localStorage.removeItem("mmec_expiry_date");
            }
            localStorage.setItem("mmec_strike_mode", strikeMode);
            if (strikeMode === "custom") {
                localStorage.setItem("mmec_strike_min", strikeMin);
                localStorage.setItem("mmec_strike_max", strikeMax);
            } else if (strikeMode === "count") {
                localStorage.setItem("mmec_strike_range", strikeRange);
            }
            if (snapshotDate)
                localStorage.setItem("mmec_snapshot_date", snapshotDate);
            else localStorage.removeItem("mmec_snapshot_date");
            setLoading(true);
            setFetchStatus("loading");
            setLogLines((prev) => [
                ...prev.slice(-(MAX_LOG_LINES - 1)),
                `[${new Date().toLocaleTimeString()}] Futures fetch: ${mappings.map((m) => `${m.future}→${m.underlying}`).join(", ")}`,
            ]);
            window.dispatchEvent(
                new CustomEvent(STABLE_FETCH_EVENT, {
                    detail: {
                        expiryRange,
                        expiryDate,
                        customExpiries,
                        snapshotDate,
                        futuresMode: true,
                    },
                }),
            );
        };

        const ft =
            language === "ar"
                ? {
                      fetch: "جلب",
                      cancel: "إلغاء",
                      tryAgain: "حاول مجدداً",
                      expiry: "انتهاء الصلاحية",
                      exactDate: "تاريخ",
                      strikes: "أسعار التنفيذ",
                      log: "سجل",
                  }
                : {
                      fetch: "Fetch",
                      cancel: "Cancel",
                      tryAgain: "Try again",
                      expiry: "Expiry",
                      exactDate: "Date",
                      strikes: "Strikes ±",
                      log: "Log",
                  };

        const futsDisabled =
            !mappings.length ||
            loading ||
            (expiryRange === "specific-date" && !expiryDate) ||
            (expiryRange === "custom" && customExpiries.length === 0);

        return (
            <>
                <div
                    style={stableStyles.shell}
                    className="flex flex-col gap-2"
                    data-tour="mmec-stable-fetch"
                >
                    <div
                        className="flex flex-wrap items-center gap-2"
                        style={{ rowGap: 8 }}
                    >
                        <span
                            className="text-sm font-semibold"
                            style={{ color: colors.text }}
                        >
                            Futures
                        </span>
                        <span
                            className="text-sm"
                            style={{ color: colors.textSecondary }}
                        >
                            {mappings.length > 0
                                ? selected.join(", ")
                                : language === "ar"
                                  ? "لا عقود محددة"
                                  : "No futures selected"}
                        </span>
                        <label className="flex flex-col gap-[5px] min-w-[120px]">
                            <span style={stableStyles.label}>{ft.expiry}</span>
                            <select
                                value={expiryRange}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setExpiryRange(v);
                                    localStorage.setItem(
                                        "mmec_expiry_range",
                                        v,
                                    );
                                    if (v !== "specific-date" && v !== "custom")
                                        localStorage.removeItem(
                                            "mmec_expiry_date",
                                        );
                                    if (v === "specific-date")
                                        ensureSpecificDate();
                                }}
                                style={{
                                    ...stableStyles.select,
                                    width: "100%",
                                }}
                            >
                                {EXPIRY_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {language === "ar"
                                            ? opt.labelAr
                                            : opt.labelEn}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {expiryRange === "specific-date" && (
                            <label className="flex flex-col gap-[5px] min-w-[140px]">
                                <span style={stableStyles.label}>
                                    {ft.exactDate}
                                </span>
                                <input
                                    type="date"
                                    value={expiryDate}
                                    onChange={(e) => {
                                        setExpiryDate(e.target.value);
                                        if (e.target.value)
                                            localStorage.setItem(
                                                "mmec_expiry_date",
                                                e.target.value,
                                            );
                                        else
                                            localStorage.removeItem(
                                                "mmec_expiry_date",
                                            );
                                    }}
                                    style={{
                                        ...stableStyles.input,
                                        width: "100%",
                                    }}
                                />
                            </label>
                        )}
                        <label
                            className="flex flex-col gap-[5px] min-w-[100px]"
                            style={{ cursor: "pointer" }}
                        >
                            <span style={stableStyles.label}>
                                {language === "ar" ? "اعتباراً من" : "As of"}
                            </span>
                            <label
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    cursor: "pointer",
                                    minHeight: 36,
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={snapshotEnabled}
                                    onChange={(e) => {
                                        const v = e.target.checked;
                                        setSnapshotEnabled(v);
                                        if (!v) {
                                            setSnapshotDate("");
                                            localStorage.removeItem(
                                                "mmec_snapshot_date",
                                            );
                                        }
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: 12,
                                        color: colors.textMuted,
                                    }}
                                >
                                    {snapshotEnabled
                                        ? language === "ar"
                                            ? "لقطة"
                                            : "snapshot"
                                        : language === "ar"
                                          ? "مباشر"
                                          : "live"}
                                </span>
                            </label>
                        </label>
                        {snapshotEnabled && (
                            <label className="flex flex-col gap-[5px] min-w-[180px]">
                                <span style={stableStyles.label}>
                                    {language === "ar"
                                        ? "التاريخ والوقت"
                                        : "Date & time"}
                                </span>
                                <input
                                    type="datetime-local"
                                    value={snapshotDate}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setSnapshotDate(v);
                                        if (v)
                                            localStorage.setItem(
                                                "mmec_snapshot_date",
                                                v,
                                            );
                                        else
                                            localStorage.removeItem(
                                                "mmec_snapshot_date",
                                            );
                                    }}
                                    style={{
                                        ...stableStyles.input,
                                        width: "100%",
                                    }}
                                />
                            </label>
                        )}
                        <label className="flex flex-col gap-[5px] min-w-[120px]">
                            <span style={stableStyles.label}>{ft.strikes}</span>
                            <select
                                value={
                                    strikeMode === "custom"
                                        ? "custom"
                                        : strikeMode === "all"
                                          ? "all"
                                          : strikeRange
                                }
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === "all") {
                                        setStrikeMode("all");
                                        localStorage.setItem(
                                            "mmec_strike_mode",
                                            "all",
                                        );
                                    } else if (v === "custom") {
                                        setStrikeMode("custom");
                                        localStorage.setItem(
                                            "mmec_strike_mode",
                                            "custom",
                                        );
                                    } else {
                                        setStrikeMode("count");
                                        setStrikeRange(v);
                                        localStorage.setItem(
                                            "mmec_strike_mode",
                                            "count",
                                        );
                                        localStorage.setItem(
                                            "mmec_strike_range",
                                            v,
                                        );
                                    }
                                }}
                                style={{
                                    ...stableStyles.select,
                                    width: "100%",
                                }}
                            >
                                {STRIKE_RANGE_OPTIONS.map((n) => (
                                    <option key={n} value={String(n)}>
                                        {n}
                                    </option>
                                ))}
                                <option value="all">
                                    {language === "ar" ? "الكل" : "All"}
                                </option>
                                <option value="custom">
                                    {language === "ar" ? "من–إلى" : "A–B"}
                                </option>
                            </select>
                        </label>
                        {strikeMode === "custom" && (
                            <>
                                <label className="flex flex-col gap-[5px] min-w-[100px]">
                                    <span style={stableStyles.label}>
                                        {language === "ar" ? "من" : "From"}
                                    </span>
                                    <input
                                        type="number"
                                        min={0}
                                        step={1}
                                        value={strikeMin}
                                        onChange={(e) => {
                                            setStrikeMin(e.target.value);
                                            localStorage.setItem(
                                                "mmec_strike_min",
                                                e.target.value,
                                            );
                                        }}
                                        style={{
                                            ...stableStyles.input,
                                            width: "100%",
                                        }}
                                        placeholder="400"
                                    />
                                </label>
                                <label className="flex flex-col gap-[5px] min-w-[100px]">
                                    <span style={stableStyles.label}>
                                        {language === "ar" ? "إلى" : "To"}
                                    </span>
                                    <input
                                        type="number"
                                        min={0}
                                        step={1}
                                        value={strikeMax}
                                        onChange={(e) => {
                                            setStrikeMax(e.target.value);
                                            localStorage.setItem(
                                                "mmec_strike_max",
                                                e.target.value,
                                            );
                                        }}
                                        style={{
                                            ...stableStyles.input,
                                            width: "100%",
                                        }}
                                        placeholder="550"
                                    />
                                </label>
                            </>
                        )}
                        <button
                            type="button"
                            onClick={handleFuturesFetch}
                            disabled={futsDisabled}
                            style={{
                                ...stableStyles.btnPrimary,
                                opacity: futsDisabled ? 0.45 : 1,
                                flexShrink: 0,
                            }}
                        >
                            {loading
                                ? language === "ar"
                                    ? "جاري الجلب…"
                                    : "Fetching…"
                                : ft.fetch}
                        </button>
                        {loading && (
                            <button
                                type="button"
                                onClick={handleCancel}
                                style={stableStyles.btnGhost}
                            >
                                {ft.cancel}
                            </button>
                        )}
                        {(fetchStatus === "error" ||
                            fetchStatus === "cancelled") && (
                            <button
                                type="button"
                                onClick={handleFuturesFetch}
                                style={{
                                    ...stableStyles.btnPrimary,
                                    backgroundColor:
                                        colors.chartPrimary || colors.primary,
                                }}
                            >
                                {ft.tryAgain}
                            </button>
                        )}
                        {expiryProgressBadge}
                    </div>
                </div>
                {fetchLogFloat}
            </>
        );
    }

    const t =
        language === "ar"
            ? {
                  fetch: "جلب",
                  cancel: "إلغاء",
                  tryAgain: "حاول مجدداً",
                  underlying: "أصل",
                  expiry: "انتهاء الصلاحية",
                  exactDate: "تاريخ",
                  strikes: "أسعار التنفيذ",
                  select: "اختر",
                  filterPlaceholder: "اكتب لتصفية الخيارات…",
                  log: "سجل",
                  more: "المزيد",
                  less: "إخفاء",
              }
            : {
                  fetch: "Fetch",
                  cancel: "Cancel",
                  tryAgain: "Try again",
                  underlying: "Underlying",
                  expiry: "Expiry",
                  exactDate: "Date",
                  strikes: "Strikes ±",
                  select: "Select",
                  filterPlaceholder: "Type to narrow choices…",
                  log: "Log",
                  more: "More",
                  less: "Less",
              };

    const fetchDisabled =
        (!selectedUnderlying && !(filterText || "").trim()) ||
        loading ||
        (expiryRange === "specific-date" && !expiryDate) ||
        (expiryRange === "custom" && customExpiries.length === 0);

    const formBody = (
        <>
            <div
                className="flex flex-wrap items-end gap-2"
                style={{ rowGap: 8 }}
            >
                {!compactLayout && (
                    <span
                        aria-hidden
                        style={{
                            ...stableStyles.label,
                            color: "var(--acc)",
                            display: "inline-flex",
                            alignItems: "center",
                            height: 32,
                            paddingRight: 4,
                            borderRight: "1px solid var(--hair)",
                            marginRight: 2,
                        }}
                    >
                        {language === "ar" ? "مستقر" : "Stable"}
                    </span>
                )}
                <div
                    className="relative flex-1 min-w-[140px] max-w-[320px]"
                    ref={typeaheadRef}
                >
                    <label className="flex flex-col gap-[5px]">
                        <span style={stableStyles.label}>{t.underlying}</span>
                        <input
                            ref={inputRef}
                            type="text"
                            value={filterText || selectedUnderlying}
                            onChange={(e) => {
                                const v = e.target.value;
                                setFilterText(v);
                                setDropdownOpen(true);
                                if (v === "") setSelectedUnderlying("");
                            }}
                            onFocus={() => setDropdownOpen(true)}
                            onBlur={() => {
                                blurTimeoutRef.current = setTimeout(
                                    () => setDropdownOpen(false),
                                    180,
                                );
                            }}
                            onKeyDown={handleTypeaheadKeyDown}
                            placeholder={t.filterPlaceholder}
                            disabled={loadingList}
                            style={{ ...stableStyles.input, width: "100%" }}
                            aria-label={t.underlying}
                            aria-autocomplete="list"
                            aria-expanded={dropdownOpen}
                        />
                    </label>
                    {dropdownOpen && filteredUnderlyings.length > 0 && (
                        <ul
                            style={stableStyles.dropdown}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            {filteredUnderlyings.map((u) => (
                                <li
                                    key={u}
                                    onClick={() => selectUnderlying(u)}
                                    style={stableStyles.dropdownItem(
                                        u === selectedUnderlying,
                                    )}
                                >
                                    {u}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <label className="flex flex-col gap-[5px] min-w-[120px]">
                    <span style={stableStyles.label}>{t.expiry}</span>
                    <select
                        value={expiryRange}
                        onChange={(e) => {
                            const v = e.target.value;
                            setExpiryRange(v);
                            localStorage.setItem("mmec_expiry_range", v);
                            if (v === "custom") {
                                // Restore any previously selected custom dates
                                const saved =
                                    localStorage.getItem("mmec_expiry_date") ||
                                    "";
                                const restored = saved
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean);
                                setCustomExpiries(restored);
                                // Lazy-load available expirations for the
                                // currently-selected underlying (or typed text).
                                const sym = (
                                    selectedUnderlying ||
                                    filterText ||
                                    ""
                                ).trim();
                                if (sym) loadAvailableExpiries(sym);
                            } else {
                                if (v !== "specific-date")
                                    localStorage.removeItem("mmec_expiry_date");
                                else ensureSpecificDate();
                                setCustomExpiries([]);
                            }
                            window.dispatchEvent(
                                new CustomEvent("stableStrikeOrExpiryChanged"),
                            );
                        }}
                        style={{ ...stableStyles.select, width: "100%" }}
                    >
                        {EXPIRY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {language === "ar" ? opt.labelAr : opt.labelEn}
                            </option>
                        ))}
                    </select>
                </label>
                {expiryRange === "specific-date" && (
                    <label className="flex flex-col gap-[5px] min-w-[140px]">
                        <span style={stableStyles.label}>{t.exactDate}</span>
                        <input
                            type="date"
                            value={expiryDate}
                            onChange={(e) => {
                                setExpiryDate(e.target.value);
                                if (e.target.value)
                                    localStorage.setItem(
                                        "mmec_expiry_date",
                                        e.target.value,
                                    );
                                else
                                    localStorage.removeItem("mmec_expiry_date");
                                window.dispatchEvent(
                                    new CustomEvent(
                                        "stableStrikeOrExpiryChanged",
                                    ),
                                );
                            }}
                            style={{ ...stableStyles.input, width: "100%" }}
                        />
                    </label>
                )}
                {expiryRange === "custom" && (
                    <CustomExpiryPanel
                        available={availableExpiries}
                        selected={customExpiries}
                        onChange={setCustomExpiries}
                        loading={customExpiriesLoading}
                        error={customExpiriesError}
                        language={language}
                        colors={colors}
                        styles={stableStyles}
                    />
                )}
                <label
                    className="flex flex-col gap-[5px] min-w-[100px]"
                    style={{ cursor: "pointer" }}
                >
                    <span style={stableStyles.label}>
                        {language === "ar" ? "اعتباراً من" : "As of"}
                    </span>
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            cursor: "pointer",
                            height: compactLayout ? 40 : 32,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={snapshotEnabled}
                            onChange={(e) => {
                                const v = e.target.checked;
                                setSnapshotEnabled(v);
                                if (!v) {
                                    setSnapshotDate("");
                                    localStorage.removeItem("mmec_snapshot_date");
                                }
                            }}
                        />
                        <span style={{ fontSize: 12, color: colors.textMuted }}>
                            {snapshotEnabled
                                ? language === "ar"
                                    ? "لقطة"
                                    : "snapshot"
                                : language === "ar"
                                  ? "مباشر"
                                  : "live"}
                        </span>
                    </label>
                </label>
                {snapshotEnabled && (
                    <label className="flex flex-col gap-[5px] min-w-[180px]">
                        <span style={stableStyles.label}>
                            {language === "ar" ? "التاريخ والوقت" : "Date & time"}
                        </span>
                        <input
                            type="datetime-local"
                            value={snapshotDate}
                            onChange={(e) => {
                                const v = e.target.value;
                                setSnapshotDate(v);
                                if (v)
                                    localStorage.setItem(
                                        "mmec_snapshot_date",
                                        v,
                                    );
                                else
                                    localStorage.removeItem(
                                        "mmec_snapshot_date",
                                    );
                            }}
                            style={{ ...stableStyles.input, width: "100%" }}
                        />
                    </label>
                )}
                <label className="flex flex-col gap-[5px] min-w-[120px]">
                    <span style={stableStyles.label}>{t.strikes}</span>
                    <select
                        value={
                            strikeMode === "custom"
                                ? "custom"
                                : strikeMode === "all"
                                  ? "all"
                                  : strikeRange
                        }
                        onChange={(e) => {
                            const v = e.target.value;
                            if (v === "all") {
                                setStrikeMode("all");
                                localStorage.setItem("mmec_strike_mode", "all");
                            } else if (v === "custom") {
                                setStrikeMode("custom");
                                localStorage.setItem(
                                    "mmec_strike_mode",
                                    "custom",
                                );
                            } else {
                                setStrikeMode("count");
                                setStrikeRange(v);
                                localStorage.setItem(
                                    "mmec_strike_mode",
                                    "count",
                                );
                                localStorage.setItem("mmec_strike_range", v);
                            }
                            window.dispatchEvent(
                                new CustomEvent("stableStrikeOrExpiryChanged"),
                            );
                        }}
                        style={{ ...stableStyles.select, width: "100%" }}
                    >
                        {STRIKE_RANGE_OPTIONS.map((n) => (
                            <option key={n} value={String(n)}>
                                {n}
                            </option>
                        ))}
                        <option value="all">
                            {language === "ar" ? "الكل" : "All"}
                        </option>
                        <option value="custom">
                            {language === "ar" ? "من–إلى" : "A–B"}
                        </option>
                    </select>
                </label>
                {strikeMode === "custom" && (
                    <>
                        <label className="flex flex-col gap-[5px] min-w-[100px]">
                            <span style={stableStyles.label}>
                                {language === "ar" ? "من" : "From"}
                            </span>
                            <input
                                type="number"
                                min={0}
                                step={1}
                                value={strikeMin}
                                onChange={(e) => {
                                    setStrikeMin(e.target.value);
                                    localStorage.setItem(
                                        "mmec_strike_min",
                                        e.target.value,
                                    );
                                }}
                                style={{ ...stableStyles.input, width: "100%" }}
                                placeholder="400"
                            />
                        </label>
                        <label className="flex flex-col gap-[5px] min-w-[100px]">
                            <span style={stableStyles.label}>
                                {language === "ar" ? "إلى" : "To"}
                            </span>
                            <input
                                type="number"
                                min={0}
                                step={1}
                                value={strikeMax}
                                onChange={(e) => {
                                    setStrikeMax(e.target.value);
                                    localStorage.setItem(
                                        "mmec_strike_max",
                                        e.target.value,
                                    );
                                }}
                                style={{ ...stableStyles.input, width: "100%" }}
                                placeholder="550"
                            />
                        </label>
                    </>
                )}
                <button
                    type="button"
                    onClick={handleFetch}
                    disabled={fetchDisabled}
                    style={{
                        ...stableStyles.btnPrimary,
                        opacity: fetchDisabled ? 0.45 : 1,
                        flexShrink: 0,
                    }}
                >
                    {loading
                        ? language === "ar"
                            ? "جاري الجلب…"
                            : "Fetching…"
                        : t.fetch}
                </button>
                {loading && (
                    <button
                        type="button"
                        onClick={handleCancel}
                        style={stableStyles.btnGhost}
                    >
                        {t.cancel}
                    </button>
                )}
                {(fetchStatus === "error" || fetchStatus === "cancelled") && (
                    <button
                        type="button"
                        onClick={handleTryAgain}
                        style={{
                            ...stableStyles.btnPrimary,
                            backgroundColor:
                                colors.chartPrimary || colors.primary,
                        }}
                    >
                        {t.tryAgain}
                    </button>
                )}
                {loadingList && (
                    <span
                        className="text-xs"
                        style={{ color: "var(--muted)", alignSelf: "center" }}
                    >
                        {language === "ar" ? "تحميل القائمة…" : "Loading list…"}
                    </span>
                )}
                {expiryProgressBadge}
                {/* Snapshot timestamp — transparency about when the data was fetched */}
                {fetchedAt && fetchStatus === "success" && (
                    <span
                        style={{
                            fontSize: 11,
                            color: colors.textMuted,
                            whiteSpace: "nowrap",
                            alignSelf: "center",
                        }}
                    >
                        {snapshotEnabled && snapshotDate
                            ? (language === "ar" ? "بيانات اعتباراً من: " : "Data as of: ") +
                              new Date(snapshotDate).toLocaleString()
                            : (language === "ar" ? "مباشر — " : "Live — ") +
                              fetchedAt.toLocaleTimeString()}
                    </span>
                )}
            </div>
        </>
    );

    if (compactLayout) {
        return (
            <>
                <div
                    style={stableStyles.shell}
                    className="flex flex-col gap-2"
                    data-tour="mmec-stable-fetch"
                >
                    <div
                        className="flex flex-wrap items-center gap-2"
                        style={{ alignItems: "center" }}
                    >
                        <span
                            className="text-sm font-semibold"
                            style={{ color: colors.text }}
                        >
                            {language === "ar" ? "جلب" : "Fetch"}
                        </span>
                        <span
                            className="text-sm font-mono truncate"
                            style={{
                                color: colors.textSecondary,
                                maxWidth: "42%",
                            }}
                        >
                            {(selectedUnderlying || filterText || "").trim() ||
                                "—"}
                        </span>
                        <button
                            type="button"
                            onClick={() => setCompactExpanded((v) => !v)}
                            style={{
                                ...stableStyles.btnGhost,
                                marginLeft: "auto",
                                minHeight: 40,
                                padding: "6px 12px",
                            }}
                            aria-expanded={compactExpanded}
                        >
                            {compactExpanded ? t.less : t.more}
                        </button>
                    </div>
                    {compactExpanded ? formBody : null}
                </div>
                {fetchLogFloat}
            </>
        );
    }

    return (
        <>
            <div
                style={stableStyles.shell}
                className="flex flex-col gap-2"
                data-tour="mmec-stable-fetch"
            >
                {formBody}
            </div>
            {fetchLogFloat}
        </>
    );
};

export default StableDataFetchBar;
